import axios from 'axios';
import toast from 'react-hot-toast';

/**
 * API 客户端配置
 * 统一处理所有 API 请求
 */

// 根据环境设置 API 基础 URL
const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://fb-ads-auto.kennjgfs.workers.dev';

// 创建 axios 实例
export const apiClient = axios.create({
	baseURL: `${API_BASE_URL}/api`,
	timeout: 30000,
	headers: {
		'Content-Type': 'application/json',
	},
});

// 请求拦截器
apiClient.interceptors.request.use(
	(config) => {
		// 添加请求时间戳
		config.metadata = { startTime: new Date() };
		
		// 如果是开发环境，打印请求信息
		if (import.meta.env.DEV) {
			console.log(`🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`);
		}
		
		return config;
	},
	(error) => {
		console.error('Request interceptor error:', error);
		return Promise.reject(error);
	}
);

// 响应拦截器
apiClient.interceptors.response.use(
	(response) => {
		// 计算请求耗时
		const endTime = new Date();
		const duration = endTime - response.config.metadata.startTime;
		
		// 如果是开发环境，打印响应信息
		if (import.meta.env.DEV) {
			console.log(`✅ API Response: ${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status} (${duration}ms)`);
		}
		
		return response;
	},
	(error) => {
		// 计算请求耗时
		const endTime = new Date();
		const duration = error.config?.metadata ? endTime - error.config.metadata.startTime : 0;
		
		// 打印错误信息
		console.error(`❌ API Error: ${error.config?.method?.toUpperCase()} ${error.config?.url} - ${error.response?.status || 'Network Error'} (${duration}ms)`, error);
		
		// 统一错误处理
		handleApiError(error);
		
		return Promise.reject(error);
	}
);

/**
 * 统一错误处理
 */
function handleApiError(error) {
	if (!error.response) {
		// 网络错误
		toast.error('网络连接失败，请检查网络设置');
		return;
	}
	
	const { status, data } = error.response;
	const errorMessage = data?.error?.message || data?.message || '请求失败';
	
	switch (status) {
		case 400:
			toast.error(`请求错误: ${errorMessage}`);
			break;
		case 401:
			toast.error('登录已过期，请重新登录');
			// 这里可以触发自动登出
			break;
		case 403:
			toast.error('权限不足，无法执行此操作');
			break;
		case 404:
			toast.error('请求的资源不存在');
			break;
		case 409:
			toast.error(`数据冲突: ${errorMessage}`);
			break;
		case 429:
			toast.error('请求过于频繁，请稍后再试');
			break;
		case 500:
			toast.error('服务器内部错误，请稍后再试');
			break;
		default:
			toast.error(`请求失败: ${errorMessage}`);
	}
}

/**
 * API 服务类
 */
export class ApiService {
	// 认证相关
	static auth = {
		login: (credentials) => apiClient.post('/auth/login', credentials),
		register: (userData) => apiClient.post('/auth/register', userData),
		logout: () => apiClient.post('/auth/logout'),
		me: () => apiClient.get('/auth/me'),
		refresh: () => apiClient.post('/auth/refresh'),
		changePassword: (passwordData) => apiClient.post('/auth/change-password', passwordData),
	};
	
	// Facebook 账户相关
	static facebook = {
		connect: (data) => apiClient.post('/facebook/connect', data),
		getAccounts: () => apiClient.get('/facebook/accounts'),
		getAccount: (id) => apiClient.get(`/facebook/accounts/${id}`),
		deleteAccount: (id) => apiClient.delete(`/facebook/accounts/${id}`),
		getPages: (accessToken) => apiClient.get('/facebook/pages', { params: { accessToken } }),
		getAdAccounts: (accessToken) => apiClient.get('/facebook/ad-accounts', { params: { accessToken } }),
	};
	
	// 广告系列相关
	static campaigns = {
		list: (params = {}) => apiClient.get('/campaigns', { params }),
		get: (id) => apiClient.get(`/campaigns/${id}`),
		create: (data) => apiClient.post('/campaigns', data),
		update: (id, data) => apiClient.put(`/campaigns/${id}`, data),
		delete: (id) => apiClient.delete(`/campaigns/${id}`),
		updateStatus: (id, status) => apiClient.post(`/campaigns/${id}/status`, { status }),
	};
	
	// AI 优化相关
	static ai = {
		getConfig: () => apiClient.get('/ai/config'),
		updateConfig: (configs) => apiClient.put('/ai/config', { configs }),
		optimize: (campaignId) => apiClient.post(`/ai/optimize/${campaignId}`),
		optimizeAll: () => apiClient.post('/ai/optimize-all'),
		getDecisions: (params = {}) => apiClient.get('/ai/decisions', { params }),
		getStats: (params = {}) => apiClient.get('/ai/stats', { params }),
		emergencyStop: (enabled) => apiClient.post('/ai/emergency-stop', { enabled }),
	};
	
	// 文件上传相关
	static upload = {
		image: (file) => {
			const formData = new FormData();
			formData.append('file', file);
			return apiClient.post('/upload/image', formData, {
				headers: {
					'Content-Type': 'multipart/form-data',
				},
			});
		},
		getFiles: (params = {}) => apiClient.get('/upload/files', { params }),
		deleteFile: (fileKey) => apiClient.delete(`/upload/files/${encodeURIComponent(fileKey)}`),
		getFileInfo: (fileKey) => apiClient.get(`/upload/files/${encodeURIComponent(fileKey)}/info`),
		batchDelete: (fileKeys) => apiClient.post('/upload/files/batch-delete', { fileKeys }),
	};
}

/**
 * React Query 查询键工厂
 */
export const queryKeys = {
	// 认证
	auth: {
		me: ['auth', 'me'],
	},
	
	// Facebook 账户
	facebook: {
		accounts: ['facebook', 'accounts'],
		account: (id) => ['facebook', 'accounts', id],
		pages: (accessToken) => ['facebook', 'pages', accessToken],
		adAccounts: (accessToken) => ['facebook', 'ad-accounts', accessToken],
	},
	
	// 广告系列
	campaigns: {
		all: ['campaigns'],
		list: (params) => ['campaigns', 'list', params],
		detail: (id) => ['campaigns', 'detail', id],
	},
	
	// AI 优化
	ai: {
		config: ['ai', 'config'],
		decisions: (params) => ['ai', 'decisions', params],
		stats: (params) => ['ai', 'stats', params],
	},
	
	// 文件上传
	upload: {
		files: (params) => ['upload', 'files', params],
		fileInfo: (fileKey) => ['upload', 'files', fileKey, 'info'],
	},
};

export default ApiService;
